/**
 * Shared chart density helpers for Overview vs Focus rendering.
 * Controls TopN slicing, label widths, and label abbreviations.
 */

export type ChartDensity = 'overview' | 'focus' | 'preview';

/** Max items shown in an overview card for ranked bar charts */
export const OVERVIEW_TOP_N = 12;

/** Ranked horizontal bar charts in library card previews (top N only) */
export const PREVIEW_RANKED_MAX = 5;

/** True for compact / catalogue-style rendering (not full focus mode) */
export function isOverviewLikeDensity(density: ChartDensity): boolean {
  return density === 'overview' || density === 'preview';
}

/**
 * Top-N cap for ranked bar chart data by density. `null` = use full dataset (focus).
 */
export function rankedBarTopN(density: ChartDensity): number | null {
  if (density === 'preview') return PREVIEW_RANKED_MAX;
  if (density === 'overview') return OVERVIEW_TOP_N;
  return null;
}

/**
 * Return the top N items from data sorted by a numeric key.
 * @param sortBy 'abs' = sort by absolute value (default), 'desc' = sort descending
 */
export function getTopN<T>(
  data: T[],
  n: number,
  key: keyof T,
  sortBy: 'abs' | 'desc' = 'abs',
): T[] {
  return [...data]
    .sort((a, b) => {
      const aVal = Number(a[key]) || 0;
      const bVal = Number(b[key]) || 0;
      return sortBy === 'abs'
        ? Math.abs(bVal) - Math.abs(aVal)
        : bVal - aVal;
    })
    .slice(0, n);
}

/** ECharts axisLabel.width in px for horizontal bar charts */
export function getLabelWidth(density: ChartDensity): number {
  if (density === 'focus') return 260;
  if (density === 'preview') return 72;
  return 140;
}

/**
 * Compact grid preset for overview (cartesian) tiles.
 * Tighter margins so plot area fills the card.
 * @param isQualityChart when true, bottom is 88 for two-line axis labels
 */
export function getCompactGrid(isQualityChart?: boolean): { top: number; right: number; bottom: number; left: number; containLabel: true } {
  return {
    top: 16,
    right: 16,
    bottom: isQualityChart ? 88 : 64,
    left: 56,
    containLabel: true,
  };
}

/** Tight cartesian margins for catalogue preview cards (pairs with catalogue `pointer-events-none` on previews) */
export function getPreviewGrid(isQualityChart?: boolean): { top: number; right: number; bottom: number; left: number; containLabel: true } {
  return {
    top: 10,
    right: 10,
    bottom: isQualityChart ? 72 : 36,
    left: isQualityChart ? 40 : 36,
    containLabel: true,
  };
}

/**
 * Focus-mode cartesian plots: consistent framing for analytics focus pages.
 */
export function getFocusCartesianGrid(extraBottom?: number): { top: number; right: number; bottom: number; left: number; containLabel: true } {
  return {
    top: 36,
    right: 32,
    bottom: 56 + (extraBottom ?? 0),
    left: 72,
    containLabel: true,
  };
}

/** Max characters before truncation for axis labels */
export function getTruncateLength(density: ChartDensity): number {
  if (density === 'focus') return 36;
  if (density === 'preview') return 10;
  return 20;
}

/** Quality issue label abbreviations for overview cards */
const QUALITY_ABBREVS: Record<string, string> = {
  'Missing predecessors': 'Missing preds',
  'Missing successors': 'Missing succs',
  'Excessive lags': 'Excess. lags',
  'Hard constraints': 'Hard constr.',
};

/** Shorten quality labels for overview; return full label in focus */
export function abbreviateQualityLabel(label: string, density: ChartDensity): string {
  if (density === 'focus') return label;
  if (density === 'preview')
    return QUALITY_ABBREVS[label] ?? (label.length > 10 ? `${label.slice(0, 9)}…` : label);
  return QUALITY_ABBREVS[label] ?? label;
}

/**
 * Compute dynamic chart height for horizontal bar charts in focus mode.
 * Each bar gets ~28px, with 80px for axes. Capped at 1200px.
 */
export function getDynamicBarHeight(itemCount: number, fallback: number): number {
  return Math.min(Math.max(fallback, itemCount * 28 + 80), 1200);
}
